import Region from '../../../constants/region';
import {
    AccessAutoMatching,
    AccessBayScheduling,
    AccessHaulerManagement,
    AccessPlantManagement,
    AccessProducerManagement,
    CanImpersonateUsers,
    Dashboard,
    GetFluidScheduleDetails,
    GetMilkScheduleDetails,
    ListAdmins,
    ListCIPWash,
    ListDrivers,
    ListDropoffs,
    ListHaulingCompanies,
    ListInvoicing,
    ListLabReports,
    ListManifests,
    ListMonthlyReports,
    ListPickups,
    ListPickupsAndLabs,
    ListProcessors,
    ListProducers,
    ListReceivers,
    ListRouteSessions,
    ListScheduleRoutes,
    ListSharedFiles,
    ListSilos,
    ListSubAdmins,
    ListSubHauling,
    ListSubProcessors,
    ListSubProducers,
    ListTrailers,
    ListUserGroups,
    ListUserGroupsV2,
    ManageAssignees,
    ManageAssignments,
    ManageBankingInfo,
    ManageClosePeriod,
    ManageCustomFields,
    ManageDeferrals,
    ManageDeliveryReports,
    ManageEquity,
    ManageFuelTracking,
    ManageHaulerPayReports,
    ManageLinkedUsers,
    ManageMerchandise,
    ManageMooRules,
    ManagePaymentStatus,
    ManagePoolingDecisions,
    ManageProcessedFluidCommodities,
    ManageProcessedFluidInvoicing,
    ManageProcessedFluidsDropoffs,
    ManageProcessedFluidsPickups,
    ManageProcessedFluidsRouteSessions,
    ViewProcessedFluidReceiving,
    ViewProcessedFluidReceivingRapidTest,
    ManageProcessorTotals,
    ManageProducerPayReports,
    ManageProducerTouchBases,
    ManageRapidTest,
    ManageSplitProducers,
    ManageVendorPayReports,
    ProducerHaulingContractsAndCharges,
    UploadDownload,
    UploadMooRules,
    ViewAccountingReports,
    ViewContracts,
    ViewDocumentation,
    ViewDriverLocations,
    ViewHaulerPayReport,
    ViewMemberCenter,
    ViewPricing,
    ViewProcessedFluidsDriverLocations,
    ViewProducerHaulingCharges,
    ViewReceivingDropoffs,
    ViewMilkPredictionReport,
    ViewContractPerformanceReport,
    ViewSupplyOutlookReport,
} from './permissions';
import LABELS from './constants';

const region = Region.US_DEMO;

const SubAdminGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard, // wrap
        UploadDownload,
        ViewContracts,
        ManageCustomFields,
        CanImpersonateUsers,
        ViewMilkPredictionReport,
        ViewContractPerformanceReport,
        ViewSupplyOutlookReport,
    ],
};

const SubProcessorGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard, // wrap
        AccessPlantManagement,
        AccessBayScheduling,
        UploadDownload,
        AccessBayScheduling,
    ],
};

const SubHaulingGeneral = {
    label: LABELS.General,
    permissions: [
        Dashboard, // wrap
        AccessHaulerManagement,
        UploadDownload,
    ],
};

const SubAdminRawMilkTracking = {
    label: LABELS.RawMilkTracking,
    permissions: [
        GetMilkScheduleDetails(), // wrap
        ListRouteSessions(region),
        ListPickups(),
        ListDropoffs(),
        ListScheduleRoutes,
        ViewReceivingDropoffs,
        ListManifests,
        ViewDriverLocations,
        ListCIPWash,
        ManageClosePeriod,
        ManageFuelTracking,
        ManageDeliveryReports,
        AccessAutoMatching,
    ],
};

const SubProcessorRawMilkTracking = {
    label: LABELS.RawMilkTracking,
    permissions: [
        GetMilkScheduleDetails(), // wrap
        ListRouteSessions(region, 'View', ''),
        ListDropoffs('View', ''),
        ViewReceivingDropoffs,
    ],
};

const SubHaulingRawMilkTracking = {
    label: LABELS.RawMilkTracking,
    permissions: [
        GetMilkScheduleDetails('View', ''), // wrap
        ListRouteSessions(region),
        ListPickups(),
        ListDropoffs(),
        ViewDriverLocations,
        ListCIPWash,
        ManageFuelTracking,
        ViewReceivingDropoffs,
    ],
};

const SubAdminProcessedFluidTracking = {
    label: LABELS.ProcessedFluidTracking,
    permissions: [
        GetFluidScheduleDetails, // wrap
        ManageProcessedFluidsPickups,
        ManageProcessedFluidsDropoffs,
        ManageProcessedFluidsRouteSessions,
        ViewProcessedFluidsDriverLocations,
        ManageProcessedFluidInvoicing,
        ManageProcessedFluidCommodities,
        ViewProcessedFluidReceiving,
        ViewProcessedFluidReceivingRapidTest,
    ],
};

const SubProcessorProcessedFluidTracking = {
    label: LABELS.ProcessedFluidTracking,
    permissions: [
        GetFluidScheduleDetails, // wrap
        ManageProcessedFluidsPickups,
        ManageProcessedFluidsDropoffs,
        ManageProcessedFluidsRouteSessions,
    ],
};

const SubHaulingProcessedFluidTracking = {
    label: LABELS.ProcessedFluidTracking,
    permissions: [
        GetFluidScheduleDetails, // wrap
        ManageProcessedFluidsPickups,
        ManageProcessedFluidsDropoffs,
        ManageProcessedFluidsRouteSessions,
        ViewProcessedFluidsDriverLocations,
    ],
};

const SubAdminQuality = {
    label: LABELS.Quality,
    permissions: [
        ListLabReports(), // wrap
        ManageRapidTest,
    ],
};

const SubProcessorQuality = {
    label: LABELS.Quality,
    permissions: [
        ManageRapidTest, // wrap
    ],
};

const SubHaulingQuality = {
    label: LABELS.Quality,
    permissions: [
        ListLabReports(), // wrap
    ],
};

const SubAdminPayroll = {
    label: LABELS.Payroll,
    permissions: [
        ManageProducerPayReports,
        ManageHaulerPayReports,
        ViewHaulerPayReport,
        ManageVendorPayReports,
        ManagePaymentStatus,
        ManageAssignments,
        ManageMooRules,
        UploadMooRules,
        ManageAssignees,
        ManageDeferrals,
        ManageMerchandise(region),
        ManageSplitProducers,
        ViewPricing,
        // force wrap
    ],
};

const SubAdminInvoicing = {
    label: LABELS.Invoicing,
    permissions: [
        ListInvoicing, // wrap
        ViewAccountingReports,
        ManageBankingInfo,
    ],
};

const SubAdminPooling = {
    label: LABELS.Pooling,
    permissions: [ManagePoolingDecisions, ManageProcessorTotals, ManageProducerTouchBases],
};

const SubProcessorInvoicing = {
    label: LABELS.Invoicing,
    permissions: [
        ListInvoicing, // wrap
    ],
};

const SubHaulingPayroll = {
    label: LABELS.Payroll,
    permissions: [
        ViewHaulerPayReport, // wrap
        ProducerHaulingContractsAndCharges,
    ],
};

const SubAdminMemberships = {
    label: LABELS.Memberships,
    permissions: [
        ViewMemberCenter, // wrap
        ListSharedFiles,
        ViewDocumentation,
        ManageEquity,
    ],
};

const SubProcessorMemberships = {
    label: LABELS.Memberships,
    permissions: [
        ListSharedFiles, // wrap
    ],
};

const SubHaulingMemberships = {
    label: LABELS.Memberships,
    permissions: [
        ListSharedFiles, // wrap
    ],
};

const SubAdminUsersAndAssets = {
    label: LABELS.UsersAndAssets,
    permissions: [
        ListAdmins, // wrap
        AccessProducerManagement,
        ListProducers,
        ListSubProducers,
        AccessPlantManagement,
        ListProcessors(region),
        ListSubProcessors(region),
        ListHaulingCompanies(region),
        ListSubHauling(region),
        AccessHaulerManagement,
        ListDrivers,
        ListUserGroups,
        ListUserGroupsV2,
        ListSubAdmins,
        ManageLinkedUsers,
        ListTrailers,
        ListSilos,
    ],
};

const SubProcessorUsersAndAssets = {
    label: LABELS.UsersAndAssets,
    permissions: [
        ListReceivers, // wrap
        ListSubProcessors(region),
        ListSilos,
    ],
};

const SubHaulingUsersAndAssets = {
    label: LABELS.UsersAndAssets,
    permissions: [
        ListDrivers, // wrap
        ListSubHauling(region),
        ListTrailers,
    ],
};

const SubProducerPermissions = {
    label: LABELS.SubProducerLabel,
    permissions: [
        Dashboard, // wrap
        ListPickups('View', ''),
        ListLabReports('View', ''),
        ListPickupsAndLabs,
        ListSharedFiles,
        AccessProducerManagement,
        ListSubProducers,
        ListMonthlyReports(region),
        ViewMemberCenter,
        ViewProducerHaulingCharges,
    ],
};

const SubAdmin = [
    SubAdminGeneral, // wrap
    SubAdminRawMilkTracking,
    SubAdminProcessedFluidTracking,
    SubAdminQuality,
    SubAdminPayroll,
    SubAdminInvoicing,
    SubAdminPooling,
    SubAdminMemberships,
    SubAdminUsersAndAssets,
];

const SubProcessor = [
    SubProcessorGeneral, // wrap
    SubProcessorRawMilkTracking,
    SubProcessorProcessedFluidTracking,
    SubProcessorQuality,
    SubProcessorInvoicing,
    SubProcessorMemberships,
    SubProcessorUsersAndAssets,
];

const SubHauling = [
    SubHaulingGeneral, // wrap
    SubHaulingRawMilkTracking,
    SubHaulingProcessedFluidTracking,
    SubHaulingQuality,
    SubHaulingPayroll,
    SubHaulingMemberships,
    SubHaulingUsersAndAssets,
];

const SubProducer = [SubProducerPermissions];

const REQUIRED_PERMISSIONS = {
    Admin: [
        ListSilos, // wrap
        ListUserGroups,
        ManageClosePeriod,
        ListInvoicing,
        ListManifests,
        GetMilkScheduleDetails(),
        ListTrailers,
    ],
};

const USDEMO = {
    Admin: { permissions: SubAdmin, required: REQUIRED_PERMISSIONS.Admin },
    'Sub-Admin': { permissions: SubAdmin },
    'Sub-Producer': { permissions: SubProducer },
    Accountant: { permissions: SubProducer },
    'Feed-Provider': { permissions: SubProducer },
    Nutritionist: { permissions: SubProducer },
    'Co-Owner': { permissions: SubProducer },
    'Sub-Processor': { permissions: SubProcessor },
    'Sub-Hauling': { permissions: SubHauling },
};

export default USDEMO;
